import { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { consumeMapContribution } from "../../reducers/map/contribution";

export function shouldShowDroppableZone(contributionState) {
  const { draggedContribution, contributionCreation } = contributionState;

  return draggedContribution || contributionCreation?.allowLocation;
}

const newContributionMapKeySelector = map =>
  Object.keys(map).find(key => map[key].contribution.newContribution?.consumed === false);

// This hook allows to fetch a potential map contribution that was positionned in the
// contribution zone and returns its latitude and longitude. It's used by the contribution
// modal form to initialize the location when the contribution is created on the server.
//
// It also consumes the contribution in the redux store, to avoid the location to be
// reused if the contribution is not provided and the user wants to create a locationless
// contribution.
//
// Note that we fetch each redux provided value from the `useSelector` calls to avoid the
// redux selector cache to be expired and the component re-rendered even when the actual
// data doesn't change, due to the hook's cache shallow comparison mechanism.
//
export const useMapContributionDetails = () => {
  const mapKey = useSelector(({ map }) => newContributionMapKeySelector(map));

  const latitude = useSelector(({ map }) => {
    const mapKey = newContributionMapKeySelector(map);
    return map[mapKey]?.contribution?.newContribution?.latLng?.lat;
  });

  const longitude = useSelector(({ map }) => {
    const mapKey = newContributionMapKeySelector(map);
    return map[mapKey]?.contribution?.newContribution?.latLng?.lng;
  });

  const explicitlyGeolocated = useSelector(({ map }) => {
    const mapKey = newContributionMapKeySelector(map);
    return map[mapKey]?.contribution?.newContribution?.explicitlyGeolocated;
  });

  // We ensure that if no contribution was found we just return null everywhere
  const newMapContributionRef = useRef(
    mapKey && {
      mapKey,
      mapContributionDetails: { latitude, longitude, explicitlyGeolocated }
    }
  );

  const mapContributionDetails = newMapContributionRef.current?.mapContributionDetails;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!newMapContributionRef.current) return;

    const { mapKey } = newMapContributionRef.current;
    dispatch(consumeMapContribution(mapKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return mapContributionDetails;
};
